import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { TabPanel, TabContext, TabList } from '@mui/lab';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import styled from '@mui/material/styles/styled';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import DOMPurify from 'dompurify';
import { cloneDeep, includes } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AdditionalParamsFormSection from './form-sections/AdditionalParamsFormSection';
import CheckboxFormSection from './form-sections/CheckboxFormSection';
import FacetParamsFormSection from './form-sections/FacetParamsFormSection';
import HighlightParamsFormSection from './form-sections/HighlightParamsFormSection';
import QueryInformationFormSection from './form-sections/QueryInformationFormSection';
import SearchParamsFormSection from './form-sections/SearchParamsFormSection';
import SemanticSearchFormSection from './form-sections/SemanticSearchFormSection';
import { defaultValues, schema } from './formValidation';
import QueryButtons from './query-buttons/QueryButtons';
import {
  formatForWebApp,
  cleanAndAssignQueryData,
  convertFormToQuery,
  convertToForm,
} from './searchFormUtils';
import SuggestForm from './SuggestForm';
import { useSearch } from '../../../contexts/SearchContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';

const StyledTabPanel = styled(TabPanel)({
  containerType: 'inline-size',
});

const SearchForm = ({
  baseUrl,
  collections,
  getQueryList,
  onCollectionChange,
  onDeleteQuery,
  onDownload,
  onEditQueryName,
  onQuerySuggest,
  onSaveQuery,
  queryFromUrl,
}) => {
  const { t } = useTranslation();
  const {
    executeSearch,
    filtersInForm,
    resetFilters,
    updateFilters,
    clearQuery,
  } = useSearch();
  const { showToast } = useToast();

  const [tab, setTab] = useState('0');
  const [parser, setParser] = useState('lucene');
  const [facet, setFacet] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [fqChips, setFqChips] = useState([]);
  const [facetQuery, setFacetQuery] = useState('');
  const [facetQueryChips, setFacetQueryChips] = useState([]);
  const [suggest, setSuggest] = useState([]);

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    control,
    trigger,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema()),
  });

  const convertQueryToForm = (query) => {
    const params = query.settings.query.jsonQuery.params;

    setFqChips(query.settings.query.jsonQuery.filter);
    setFacetQueryChips(params['facet.query']);
    setFilterQuery('');
    setFacetQuery('');
    setHighlight(params.hl);
    setFacet(params.facet);
    setParser(params.defType);

    // This reverts the changes made to the form object that are done in the convertFormToQuery function above.
    // This is so it can be easily merged into the form object passed to react-hook-forms
    convertToForm(query);

    setValue('settings', query.settings);
  };

  useEffect(() => {
    if (queryFromUrl) {
      executeSearch(queryFromUrl.settings.query);
      convertQueryToForm(queryFromUrl);
    }
  }, []);

  const collection = watch('settings.query.collection');
  const dense = watch('settings.query.jsonQuery.params.dense');

  const handleTabChange = (e, value) => setTab(value);

  const handleSaveQuery = async (name) => {
    const validForm = await trigger();
    if (validForm) {
      const formValues = getValues();
      const formCopy = cloneDeep(formValues);
      const cleanForm = convertFormToQuery(formCopy, fqChips, facetQueryChips);

      cleanForm.name = name.queryName;
      cleanForm.collection = cleanForm.settings.query.collection;

      await onSaveQuery(cleanAndAssignQueryData(cleanForm));
    } else {
      showToast(TOAST_TYPE.ERROR, t('invalidSearchQuery', { ns: 'errors' }));
    }
  };

  const handleLoadQuery = async (query) => {
    clearErrors();
    reset();
    await clearQuery();
    await resetFilters();
    executeSearch(query.settings.query);
    convertQueryToForm(query);
  };

  const handleDeleteQuery = (queryId) => onDeleteQuery(queryId);
  const handleParserChange = (event) => setParser(event.target.value);
  const handleFacetCheck = (event) => setFacet(event.target.checked);
  const handleHighlightCheck = (event) => setHighlight(event.target.checked);
  const fqChange = (e) => setFilterQuery(e.target.value);
  const facetQueryChange = (e) => setFacetQuery(e.target.value);
  const handleAddChip = (field) => {
    if (field === 'fq') {
      if (!filterQuery || includes(fqChips, filterQuery)) return;
      updateFilters(filterQuery);
      setFilterQuery('');
    } else {
      if (!facetQuery || includes(facetQueryChips, facetQuery)) return;
      const currentChips = facetQueryChips || [];

      setFacetQueryChips([...currentChips, facetQuery]);
      setFacetQuery('');
    }
  };
  const handleDeleteChip = (field, chipToDelete) => {
    if (field === 'fq') {
      updateFilters(chipToDelete);
    } else {
      const removedChipList = facetQueryChips.filter(
        (chip) => chip !== chipToDelete
      );
      setFacetQueryChips(removedChipList);
    }
  };

  const onSubmit = (data) => {
    const formCopy = cloneDeep(data);
    const cleanedForm = convertFormToQuery(formCopy, fqChips, facetQueryChips);
    // clean empty df field or else Solr will return a 400 error
    const cleanedQuery = cleanAndAssignQueryData(cleanedForm).settings.query;
    executeSearch(cleanedQuery);
  };

  const handleResetForm = async () => {
    reset();
    await resetFilters();

    setValue('settings.query.jsonQuery.params.defType', 'lucene');
    setValue('settings.query.collection', '');
    setValue('settings.query.jsonQuery.params.op', 'OR');
    setParser('lucene');
    setHighlight(false);
    setFacet(false);
    setFilterQuery('');
    setFacetQuery('');
    setFacetQueryChips([]);
  };

  /**
   * Transforms the suggestion list from soldier into an object to display in the autocomplete.
   * @param {String} query - The user-typed or selected query in entered into the search form
   * @param {Array<Object>} suggestList - The returned list of suggestions from solr.
   * @returns The array of objects for each suggestion. Each object contains the name of the dictionary (for grouping), a label, and the term. The term may include highlight tags, whereas the label will not.
   */
  const transformSuggestOptions = (query, suggestList) =>
    suggestList.map((dictionaryResult) =>
      dictionaryResult[1][query].suggestions.map((suggestion) => ({
        dictionary: dictionaryResult[0],
        label: suggestion.term.replaceAll(/<\/?b>/g, ''),
        term: suggestion.term,
      }))
    );

  const handleQuerySuggest = async (query) => {
    const suggestForm = getValues([
      'settings.suggest.collection',
      'settings.suggest.requestHandler',
      'settings.suggest.dictionary',
    ]);

    if (!suggestForm[0] || !suggestForm[1] || !suggestForm[2]) return;

    const dictionaryMap = suggestForm[2].split(/\s+|,\s+|,/g);
    const suggestRes = await onQuerySuggest(
      query,
      suggestForm[0],
      suggestForm[1],
      dictionaryMap
    );

    if (!suggestRes) return;

    const suggestList = suggestRes.solrResponse.suggest;
    const formatSuggestions = transformSuggestOptions(
      query,
      Object.entries(suggestList)
    );

    setSuggest(formatSuggestions.flat());
  };

  useEffect(() => {
    setFqChips(filtersInForm);
  }, [filtersInForm]);

  const handleDownloadWebApp = async () => {
    if (!baseUrl) {
      showToast(
        TOAST_TYPE.ERROR,
        t('webAppDomainError', { ns: 'notifications' })
      );
      return;
    }

    const form = getValues();
    const formattedForm = formatForWebApp(
      baseUrl,
      form,
      fqChips,
      facetQueryChips
    );

    const { collection, idField, qt } = formattedForm;

    if (collection && idField && qt) {
      await onDownload(formattedForm);
    } else {
      showToast(
        TOAST_TYPE.ERROR,
        t('webAppFormError', { ns: 'notifications' })
      );
    }
  };

  const handleCollectionChange = async () => {
    const uniqueKey = await onCollectionChange(collection);
    setValue('settings.query.uniqueKey', uniqueKey, { shouldValidate: true });
  };

  useEffect(() => {
    if (!collection) return;
    handleCollectionChange();
  }, [collection]);

  return (
    <Container>
      <QueryButtons
        getQueryList={getQueryList}
        onDeleteQuery={handleDeleteQuery}
        onDownload={handleDownloadWebApp}
        onEditQueryName={onEditQueryName}
        onLoadQuery={handleLoadQuery}
        onReset={handleResetForm}
        onSaveQuery={handleSaveQuery}
      />

      <form onSubmit={handleSubmit(onSubmit)} aria-label="search-form">
        <TabContext value={tab}>
          <TabList
            variant="fullWidth"
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Query" value="0" />
            <Tab label="Suggest" value="1" />
          </TabList>

          <StyledTabPanel value="0">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                defaultValue={''}
                name="settings.query.jsonQuery.query"
                render={({ field: { onChange, ...field } }) => (
                  <Autocomplete
                    {...field}
                    disableClearable
                    // This is necessary because normally the mui component will only show values
                    // that are a substring of the input. We don't care about that and only deliver what
                    // solr gives us, so we need to show all values.
                    filterOptions={(x) => x}
                    freeSolo
                    fullWidth
                    groupBy={(option) => option.dictionary}
                    // Sets rhf value when selecting a dropdown suggestion.
                    onChange={(e, data) => onChange(data?.label || '')}
                    onInputChange={async (e, val, reason) => {
                      await handleQuerySuggest(val);
                      // sets rhf value when typing in query input.
                      if (reason === 'input') onChange(val);
                    }}
                    options={suggest}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        variant="outlined"
                        error={!!errors?.settings?.query?.jsonQuery?.query}
                        helperText={
                          errors?.settings?.query?.jsonQuery?.query?.message
                        }
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'query',
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const term = {
                        __html: DOMPurify.sanitize(option.term, {
                          ALLOWED_TAGS: ['b'],
                        }),
                      };
                      return <Box {...props} dangerouslySetInnerHTML={term} />;
                    }}
                  />
                )}
              />
              <Button
                style={{ marginLeft: '1em', height: 40 }}
                type="submit"
                variant="contained"
                color="primary"
                aria-label="search"
              >
                {t('search', { ns: 'buttons' })}
              </Button>
            </Box>

            <QueryInformationFormSection
              control={control}
              errors={errors}
              collections={collections}
            />

            <SemanticSearchFormSection
              control={control}
              dense={dense}
              errors={errors}
            />

            <SearchParamsFormSection
              control={control}
              errors={errors}
              filterQuery={filterQuery}
              fqChange={fqChange}
              fqChips={fqChips}
              onAddChip={handleAddChip}
              onDeleteChip={handleDeleteChip}
              onParserChange={handleParserChange}
            />

            {parser !== 'lucene' && (
              <AdditionalParamsFormSection
                control={control}
                errors={errors}
                parser={parser}
              />
            )}

            <CheckboxFormSection
              parser={parser}
              control={control}
              onHighlightCheck={handleHighlightCheck}
              onFacetCheck={handleFacetCheck}
            />

            {highlight && (
              <HighlightParamsFormSection control={control} errors={errors} />
            )}

            {facet && (
              <FacetParamsFormSection
                control={control}
                errors={errors}
                facetQuery={facetQuery}
                facetQueryChips={facetQueryChips}
                facetQueryChange={facetQueryChange}
                onAddChip={handleAddChip}
                onDeleteChip={handleDeleteChip}
              />
            )}
          </StyledTabPanel>

          <TabPanel value="1">
            <SuggestForm
              control={control}
              errors={errors}
              collections={collections}
            />
          </TabPanel>
        </TabContext>
      </form>
    </Container>
  );
};

SearchForm.propTypes = {
  baseUrl: PropTypes.string,
  collections: PropTypes.array.isRequired,
  getQueryList: PropTypes.func.isRequired,
  onCollectionChange: PropTypes.func.isRequired,
  onDeleteQuery: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onEditQueryName: PropTypes.func.isRequired,
  onQuerySuggest: PropTypes.func.isRequired,
  onSaveQuery: PropTypes.func.isRequired,
  queryFromUrl: PropTypes.object,
};

export default SearchForm;
